import { forwardRef, useEffect, useImperativeHandle } from 'react';
import { FlexBox, FlexBoxDirection, Input, Label, Option, Select } from '@ui5/webcomponents-react';
import { SaveResult, SpecificQueryType } from 'components/shared/interfaces/SpecificQueryTypes';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useBaseQueryStore } from 'stores/CreateBaseQueryStore';
import { useNotesQueryStore } from 'stores/CreateNotesQueryStore';
import { validateSAPUserId, validateString } from 'utils/validator';
import { useStyles } from '../CreateQueryView/CreateQueryView.jss';
import { DocumentType, IssueType, useCreateNotesMutation, Worklist } from 'generated/graphql';
import isMatch from 'lodash/isMatch';
import { commonNotesQueryInitialStateFields } from 'stores/slices/NotesQuerySlices/CommonNotesQuerySlice';
import { WorklistOptions } from 'stores/interfaces/CommonQueryState';
import { compareAlphabetically } from 'utils/sorting';
import { NotesProjects } from 'enum/NotesProjects';
import { useCreateQueryFreezeStore } from 'stores/CreateQueryFreezeStore';

import ValueState from '@ui5/webcomponents-base/dist/types/ValueState.js';
import WrappingType from '@ui5/webcomponents/dist/types/WrappingType.js';

export const NotesQuery = forwardRef<SpecificQueryType>((_, ref) => {
  const { t } = useTranslation();
  const classes = useStyles();

  const store = useNotesQueryStore();
  const freezeStore = useCreateQueryFreezeStore();
  const baseQueryStore = useBaseQueryStore();
  const {
    deadline,
    details,
    projectManager,
    selectedIssueType,
    selectedProject: selectedProjectId,
    sourceText,
    targetLanguage,
    targetText,
    attachments
  } = baseQueryStore;

  /** GraphQL Queries/Mutations */
  const [{ fetching }, callCreateQuery] = useCreateNotesMutation();

  const setDocumentTypeByProject = () => {
    const selectedProject = baseQueryStore.projectNames?.data?.projects.find(
      (project) => Number(project.id) === selectedProjectId
    );
    let documentType: DocumentType = '' as DocumentType;
    if (selectedProject?.name === NotesProjects.NOTES) {
      documentType = DocumentType.SAP_NOTE;
    } else if (selectedProject?.name === NotesProjects.KBA) {
      documentType = DocumentType.KBA;
    }
    store.setDocumentType(documentType);
  };

  useImperativeHandle(ref, () => ({
    save: () => {
      return onSave();
    },
    cancel: () => {
      store.resetData();
    },
    isValid: () => {
      return validateAllFields();
    },
    containsRelevantChanges: () => {
      return containsRelevantChanges();
    }
  }));

  useEffect(() => {
    store.setFetching(fetching);

    return () => store.setFetching(false);
  }, [fetching]);

  useEffect(() => {
    setDocumentTypeByProject();
  }, [selectedProjectId]);

  const validateDocumentNumber = () => {
    return validateString(store.documentNumber, store.setDocumentNumberValueState);
  };

  const validateComponent = () => {
    return validateString(store.component, store.setComponentValueState);
  };

  const validateDocumentResponsible = (value = store.documentResponsible) => {
    if (store.documentType === DocumentType.SAP_NOTE) {
      const isNotEmpty = validateString(value || '', store.setDocumentResponsibleValueState);
      if (!isNotEmpty) return false;
    }
    if (value) {
      return validateSAPUserId(value, store.setDocumentResponsibleValueState);
    } else {
      store.setDocumentResponsibleValueState(ValueState.None);
    }
    return true;
  };

  const validateAllFields = () => {
    const isDocumentNumberValid = validateDocumentNumber();
    const isComponentValid = validateComponent();
    const isDocumentResponsibleValid = validateDocumentResponsible();

    return isDocumentNumberValid && isComponentValid && isDocumentResponsibleValid;
  };

  const onSave = async (): Promise<SaveResult> => {
    const result = await callCreateQuery({
      input: {
        deadline: dayjs(deadline).format('YYYY-MM-DD'),
        issueType: selectedIssueType as IssueType,
        project: selectedProjectId,
        sourceText: sourceText,
        targetLanguage: targetLanguage,
        details: details,
        pmName: projectManager,
        targetText: targetText,
        documentNumber: store.documentNumber,
        component: store.component,
        documentType: store.documentType,
        worklist:
          store.worklist === WorklistOptions.NOT_SELECTED_WORKLIST
            ? null
            : (store.worklist as Worklist),
        documentResponsible: store.documentResponsible?.toUpperCase()
      },
      files: attachments
    });
    const success = !result.error;
    if (success) {
      store.resetData();
      return { id: result?.data?.query.id };
    }
    return { error: result.error };
  };

  const containsRelevantChanges = () => {
    return !isMatch(store, commonNotesQueryInitialStateFields);
  };

  const onChangeDocumentNumber = (event: CustomEvent) => {
    const { value } = event.target as HTMLInputElement;
    store.setDocumentNumber(value);
    validateString(value, store.setDocumentNumberValueState);
  };

  const onChangeComponent = (event: CustomEvent) => {
    const { value } = event.target as HTMLInputElement;
    store.setComponent(value);
    validateString(value, store.setComponentValueState);
  };

  const onChangeWorklist = (event: CustomEvent) => {
    const { value } = event.detail.selectedOption.dataset;
    store.setWorklist(value);
  };

  const onChangeDocumentResponsible = (event: CustomEvent) => {
    const { value } = event.target as HTMLInputElement;
    store.setDocumentResponsible(value);
    validateDocumentResponsible(value);
  };

  const renderDocumentResponsible = (labelClassName: string, required: boolean = false) => {
    return (
      <>
        <Label
          id="document-responsible-label"
          className={labelClassName}
          showColon
          required={required}
          title={t('document-responsible-tooltip')}
          wrappingType="None"
        >
          <span data-help-id="create-query-notes-document-responsible">
            {t('document-responsible-label')}
          </span>
        </Label>
        <Input
          aria-labelledby="document-responsible-label"
          valueState={store.documentResponsibleValueState}
          valueStateMessage={
            <Label wrappingType={WrappingType.Normal}>
              {t('display-value-state-message-document-responsible')}
            </Label>
          }
          onChange={onChangeDocumentResponsible}
          className={classes.formInput}
          value={store.documentResponsible || ''}
          disabled={freezeStore.documentResponsible}
          placeholder={t('document-responsible-placeholder')}
          title={t('document-responsible-tooltip')}
        ></Input>
      </>
    );
  };
  return (
    <>
      <FlexBox direction={FlexBoxDirection.Column}>
        <div className={classes.div}>
          <Label
            id="document-number-label"
            className={classes.label}
            showColon
            required
            wrappingType="None"
          >
            <span data-help-id="create-query-notes-document-number">
              {t('document-number-label')}
            </span>
          </Label>
          <Input
            aria-labelledby="document-number-label"
            valueState={store.documentNumberValueState}
            valueStateMessage={
              <Label wrappingType={WrappingType.Normal}>
                {t('display-value-state-message-document-number')}
              </Label>
            }
            onChange={onChangeDocumentNumber}
            className={classes.formInput}
            value={store.documentNumber}
            disabled={freezeStore.documentNumber}
          ></Input>

          <Label
            id="component-label"
            className={classes.secondColumnLabel}
            showColon
            required
            wrappingType="None"
          >
            <span data-help-id="create-query-notes-component">{t('component-label')}</span>
          </Label>
          <Input
            aria-labelledby="component-label"
            valueState={store.componentValueState}
            valueStateMessage={
              <Label wrappingType={WrappingType.Normal}>
                {t('display-value-state-message-application-component')}
              </Label>
            }
            onChange={onChangeComponent}
            className={classes.formInput}
            value={store.component}
            disabled={freezeStore.component}
          ></Input>
        </div>
      </FlexBox>
      {store.documentType === DocumentType.SAP_NOTE && (
        <FlexBox direction={FlexBoxDirection.Column}>
          <div className={classes.div}>
            <Label id="worklist-label" className={classes.label} showColon wrappingType="None">
              <span data-help-id="create-query-worklist">{t('worklist-label')}</span>
            </Label>
            <Select
              aria-labelledby="worklist-label"
              className={classes.formInput}
              onChange={onChangeWorklist}
              valueState={store.worklistValueState}
              valueStateMessage={
                <Label wrappingType={WrappingType.Normal}>
                  {t('display-value-state-message-worklist')}
                </Label>
              }
              title={t(store.worklist)}
              disabled={freezeStore.worklist}
            >
              {Object.values(WorklistOptions)
                .sort((a, b) => compareAlphabetically(t(a), t(b)))
                .map((worklist) => (
                  <Option
                    key={worklist}
                    data-value={worklist}
                    selected={worklist === store.worklist}
                  >
                    {t(worklist)}
                  </Option>
                ))}
            </Select>
            {renderDocumentResponsible(classes.secondColumnLabel, true)}
          </div>
        </FlexBox>
      )}
      {store.documentType === DocumentType.KBA && (
        <FlexBox direction={FlexBoxDirection.Column}>
          <div className={classes.div}>{renderDocumentResponsible(classes.label)}</div>
        </FlexBox>
      )}
    </>
  );
});
